import {
  getQueryParam,
  setQueryParam as _setQueryParam,
} from '@app/utils/url-helpers'
import api from './api/pdf-annotations'
import {
  ANNOTATION_DESELECTED_ACTION,
  ANNOTATION_SELECTED_ACTION,
  ANNOTATION_HIGHLIGHT_COLOR,
  ANNOTATION_RECTANGLE_TOOL,
  ANNOTATION_RECTANGLE_FILL_COLOR_OPAQUE,
  ANNOTATION_RECTANGLE_FILL_OPACITY,
  ANNOTATION_COMMENT_CONTEXTUAL_BUTTON,
  ANNOTATION_COMMENT_BUTTON,
  ANNOTATION_CHANGED_ACTIONS,
} from '../constants/pdf-annotations'
import { REMARK_ACTIONS } from '@app/application/communication/constants'

import PDFTronAnnotationsUI from './pdftron-annotations-ui'
import PDFTronAnnotationsUtils from './pdftron-annotations-utils'

const setQueryParam = (key, value) => {
  _setQueryParam(key, value, { replace: true })
}

class PDFTronAnnotationsManager {
  constructor(
    pdfTronInstance,
    ownerId,
    ownerType,
    {
      enableAnnotationsToolbar = false,
      focusAnnotationFromUrl = true,
      showAnnotationBubbles = true,
      showCommunicationToggle = true,
    } = {}
  ) {
    const { annotationManager, documentViewer } = pdfTronInstance.Core

    this.instance = pdfTronInstance
    this.annotationManager = annotationManager
    this.documentViewer = documentViewer
    this.ownerId = ownerId
    this.ownerType = ownerType
    this.enableAnnotationsToolbar = enableAnnotationsToolbar
    // Deep-linking to an annotation via the `annotationId` query param or a
    // `#remark-<id>` hash auto-selects it on load, which opens its comment.
    // That is intended for the full-page document viewer; secondary previews
    // (e.g. the file-attachments sidebar) opt out so opening a preview doesn't
    // pop a comment from a stale/global URL param.
    this.focusAnnotationFromUrl = focusAnnotationFromUrl
    // Ordinal "bubbles" drawn next to annotations with remarks. Re-rendered on
    // every page-complete event (including resize). Secondary previews opt out
    // so they never render — otherwise they appear inconsistently (only after a
    // resize triggers a redraw).
    this.showAnnotationBubbles = showAnnotationBubbles
    // Header button that toggles the app's right-hand communication drawer.
    // Only meaningful in the full-page document viewer; secondary previews
    // (e.g. the file-attachments sidebar) opt out so the preview header doesn't
    // expose a control that drives the surrounding app's drawer.
    this.showCommunicationToggle = showCommunicationToggle
    this.apiAnnotations = []

    this.utils = new PDFTronAnnotationsUtils(this)
    this.UI = new PDFTronAnnotationsUI(this)

    // bind "this" properly, these fns are used as event listener handlers
    this.fetchAnnotations = this.fetchAnnotations.bind(this)
    this.ensureFocusAnnotation = this.ensureFocusAnnotation.bind(this)
    this.deselectAllAnnotations = this.deselectAllAnnotations.bind(this)
    this.handleRemarkModalClosed = this.handleRemarkModalClosed.bind(this)
    this.handleDeletedAnnotation = this.handleDeletedAnnotation.bind(this)

    this.onCommentButtonClick = this._onCommentButtonClick.bind(this)
    this.onHighlightedTextCommentButtonClick =
      this._onHighlightedTextCommentButtonClick.bind(this)
  }

  setup() {
    this.UI.setup()
  }

  async fetchAnnotations() {
    try {
      this.apiAnnotations = await api.getAnnotations({
        ownerId: this.ownerId,
        ownerType: this.ownerType,
      })
    } catch (e) {
      this.utils.handleError(e, { request: 'fetchAnnotations' })
    }

    this._lockAnnotations()
    if (this.focusAnnotationFromUrl) {
      this._selectAnnotationById(this._annotationIdFromUrl())
    }

    this.UI.redrawCurrentPage()

    return this.apiAnnotations
  }

  handleDeletedAnnotation(refreshCommunicationDataPayload = {}) {
    const { action, remark } = refreshCommunicationDataPayload
    if (action !== REMARK_ACTIONS.DESTROYED) {
      return
    }

    const apiAnnotationForRemark = this._apiAnnotationByRemarkId(remark.id)
    if (!apiAnnotationForRemark) {
      return
    }

    const annotationToDelete = this.annotationManager.getAnnotationById(
      apiAnnotationForRemark.xfdfId
    )
    if (annotationToDelete) {
      this.annotationManager.deleteAnnotation(annotationToDelete)
    }
  }

  handleTextSelected({ quads, pageNumber }) {
    if (!quads || quads.length === 0) {
      return
    }

    this.lastTextSelectionQuads = quads
    this.lastTextSelectionPageNumber = pageNumber
    this.UI.updateAnnotationPopupStyles(ANNOTATION_COMMENT_CONTEXTUAL_BUTTON)
  }

  handleRemarkModalClosed() {
    const selectedAnnotation = this._selectedAnnotation()
    if (!selectedAnnotation) {
      return
    }

    const apiAnnotation = this._apiAnnotationByXfdfId(selectedAnnotation.Id)

    if (!apiAnnotation || !apiAnnotation.remarkId) {
      this.annotationManager.deleteAnnotation(selectedAnnotation)
    }
  }

  handleDocumentLoaded() {
    this.UI.enableHeader()
    this.UI.setupToolStyles()
  }

  handleAnnotationsLoaded() {
    this._lockAnnotations()

    if (this.focusAnnotationFromUrl) {
      this.focusAnnotationById(this._annotationIdFromUrl())
    }

    this.UI.redrawCurrentPage()
    this.UI.markIframeReady()
  }

  handlePageComplete(pageNumber, canvas) {
    if (!this.showAnnotationBubbles) {
      return
    }

    const annotationsWithRemark = this.apiAnnotations.reduce((acc, annot) => {
      if (annot.remarkId) {
        acc.push(annot.xfdfId)
      }
      return acc
    }, [])

    const annotations = this.annotationManager
      .getAnnotationsList()
      .filter((annot) => {
        return (
          annot.PageNumber === pageNumber &&
          annotationsWithRemark.includes(annot.Id)
        )
      })

    this.UI.drawBubbles(canvas, annotations)
  }

  handleAnnotationSelected({ annotations, action }) {
    const annotation = annotations[0]
    const apiAnnotation = this._apiAnnotationByXfdfId(annotation.Id)

    if (action === ANNOTATION_SELECTED_ACTION && annotations.length === 1) {
      this.UI.updateAnnotationPopupStyles(ANNOTATION_COMMENT_BUTTON)
      annotation.disableRotationControl()

      const { remarkId } = apiAnnotation || {}

      if (remarkId) {
        window.eventHub.$emit('openSidepane')
        window.eventHub.$emit('highlightRemark', {
          elementId: `remark-${remarkId}`,
          force: true,
        })
      }

      setQueryParam('annotationId', annotation.Id)
    }

    if (action === ANNOTATION_DESELECTED_ACTION) {
      if (!apiAnnotation) {
        this.annotationManager.deleteAnnotation(annotation)
      }

      setQueryParam('annotationId', null)
      window.eventHub.$emit('clearHighlightedCard')
    }
  }

  async handleAnnotationChanged({ annotations, action, imported }) {
    if (imported || action === ANNOTATION_CHANGED_ACTIONS.DELETED) {
      return
    }

    const annotation = annotations[0]

    switch (action) {
      case ANNOTATION_CHANGED_ACTIONS.ADDED:
        this._handleAnnotationAdded(annotation)
        break
      case ANNOTATION_CHANGED_ACTIONS.UPDATED:
        await this._handleAnnotationUpdated(annotation)
        await this.fetchAnnotations()
        break
      default:
        this.utils.handleError(
          new Error('Unexpected action in handleAnnotationChanged'),
          { action }
        )
    }
  }

  ensureFocusAnnotation(id) {
    this.deselectAllAnnotations()
    this.focusAnnotationById(id)
  }

  focusAnnotationById(id) {
    const annotation = this.annotationManager.getAnnotationById(id)
    if (!annotation) {
      return
    }

    this.documentViewer.setCurrentPage(annotation.PageNumber)

    this.annotationManager.selectAnnotation(annotation)
  }

  deselectAllAnnotations() {
    this.annotationManager.deselectAllAnnotations()
  }

  _selectAnnotationById(id) {
    const annotation = this.annotationManager.getAnnotationById(id)
    if (!annotation) {
      return
    }

    this.annotationManager.selectAnnotation(annotation)
  }

  _annotationIdFromUrl() {
    const goToAnnotationId = getQueryParam('annotationId')
    if (goToAnnotationId) {
      return goToAnnotationId
    }
    // select annotation by remarkId (from inbox)
    const hashPieces = window.location.hash.split('-')
    if (hashPieces[0] !== '#remark') {
      return
    }

    const remarkId = parseInt(hashPieces[1])
    const apiAnnotation = this._apiAnnotationByRemarkId(remarkId)
    if (apiAnnotation) {
      return apiAnnotation.xfdfId
    }
  }

  _handleAnnotationAdded(annotation) {
    this._fixAnnotationColorAndOpacity(annotation)
    this.annotationManager.selectAnnotation(annotation)
  }

  async _handleAnnotationUpdated(annotation) {
    const xfdfString = await this._exportAnnotationXFDF()
    if (!xfdfString) {
      return
    }

    const xfdfId = annotation.Id

    const apiAnnotation = this._apiAnnotationByXfdfId(xfdfId)

    try {
      if (apiAnnotation) {
        await api.updateAnnotation({ id: apiAnnotation.id, xfdfString })
      }
    } catch (e) {
      this.utils.handleError(e, {
        request: 'updateAnnotation',
        action: 'update',
      })
    }
  }

  _fixAnnotationColorAndOpacity(annotation) {
    if (annotation.ToolName === ANNOTATION_RECTANGLE_TOOL) {
      // the Rectangle tool FillColor is some yellow + opacity.
      // however, the XFDF standard doesn't support opacity in colors, so we need to
      // reset the FillColor to the opaque yellow, and set the Opacity property
      // which will generate the correct XFDF representation, while also using the correct color while drawing
      annotation.FillColor = this.utils.colorFromRGB(
        ANNOTATION_RECTANGLE_FILL_COLOR_OPAQUE
      )
      annotation.Opacity = ANNOTATION_RECTANGLE_FILL_OPACITY
    }
  }

  async _openRemarkModalForNewAnnotation() {
    const selectedAnnotation = this._selectedAnnotation()
    if (!selectedAnnotation) {
      return
    }

    const xfdfString = await this._exportAnnotationXFDF()
    if (!xfdfString) {
      return
    }

    const payload = {
      pdfAnnotation: {
        xfdfId: selectedAnnotation.Id,
        xfdfString,
      },
    }

    window.eventHub.$emit('openNewRemarkModal', payload)
  }

  _openRemarkModalForAnnotation(apiAnnotation) {
    const payload = { annotationId: apiAnnotation.id }

    // 1. created by current user: open edit remark modal
    // 2. created by a different user: focus comment form on existing remark
    if (String(apiAnnotation.userId) === Silverfin.currentUserId()) {
      payload.remarkId = apiAnnotation.remarkId
      window.eventHub.$emit('openEditRemarkModal', payload)
    } else {
      window.eventHub.$emit('openSidepane')
      window.eventHub.$emit('highlightRemark', {
        elementId: `remark-${apiAnnotation.remarkId}`,
        force: true,
      })
      window.eventHub.$emit('focusCommentForm', {
        commentableId: apiAnnotation.remarkId,
        commentableType: 'Remark',
      })
    }
  }

  async _onCommentButtonClick() {
    const selectedAnnotation = this._selectedAnnotation()
    if (!selectedAnnotation) {
      return
    }

    const apiAnnotation = this._apiAnnotationByXfdfId(selectedAnnotation.Id)
    if (apiAnnotation) {
      this._openRemarkModalForAnnotation(apiAnnotation)
    } else {
      // we selected an annotation, but it's not actually in the API annotations -> create a new one
      await this._openRemarkModalForNewAnnotation()
    }
  }

  async _onHighlightedTextCommentButtonClick() {
    const annot = new this.instance.Core.Annotations.TextHighlightAnnotation({
      Quads: this.lastTextSelectionQuads,
      PageNumber: this.lastTextSelectionPageNumber,
      StrokeColor: this.utils.colorFromRGB(ANNOTATION_HIGHLIGHT_COLOR),
    })
    this.documentViewer.clearSelection()
    this.annotationManager.addAnnotation(annot)
    this.annotationManager.redrawAnnotation(annot)
    await this._openRemarkModalForNewAnnotation()
  }

  _lockAnnotations() {
    const currentUserId = Silverfin.currentUserId()

    const otherUsersApiAnnotationIds = this.apiAnnotations.reduce(
      (results, apiAnnotation) => {
        if (String(apiAnnotation.userId) !== currentUserId) {
          results.push(apiAnnotation.xfdfId)
        }

        return results
      },
      []
    )

    this.annotationManager.getAnnotationsList().forEach((annotation) => {
      if (otherUsersApiAnnotationIds.includes(annotation.Id)) {
        annotation.NoResize = true
      }
    })
  }

  _apiAnnotationByXfdfId(xfdfId) {
    return this.apiAnnotations.find((a) => a.xfdfId === xfdfId)
  }

  _apiAnnotationByRemarkId(remarkId) {
    return this.apiAnnotations.find((a) => a.remarkId === remarkId)
  }

  async _exportAnnotationXFDF() {
    try {
      return await this.annotationManager.exportAnnotationCommand()
    } catch (e) {
      this.utils.handleError(e, { source: 'pdfTronExport' })
      return null
    }
  }

  _selectedAnnotation() {
    return this.annotationManager.getSelectedAnnotations()[0]
  }
}

export default PDFTronAnnotationsManager
